<template>
  <div class="row gx-3">
    <!-- Левая колонка с обложкой и информацией -->
    <div class="col-12 audio-player-col ps-2">
      <div
        class="bg-secondary text-center bg-opacity-05 px-3 pt-5"
        style="min-height: 460px"
      >
        <!-- poster  -->
        <div style="width: 110px" class="mt-3 m-auto">
          <img
            v-if="currentTrack?.coverUrl"
            :src="currentTrack.coverUrl"
            alt=""
            class="w-100"
          />
          <img
            v-else-if="currentTrack?.src?.poster && currentTrack?.src?.folder"
            :src="$getWebImage(currentTrack.src.poster, currentTrack.src.folder)"
            alt=""
            class="w-100"
          />
          <div
            class="w-100 d-flex align-items-center justify-content-center display-3 text-danger"
            style="aspect-ratio: 1 / 1.4"
            v-else
          >
            <IconComponent lib="filoxenia" icon="MusicNote" />
          </div>
        </div>

        <!-- name  -->
        <p class="mt-5">
          <span v-if="currentTrack?.name">{{ currentTrack.name }}</span>
          <span v-else>ㅤ</span>
        </p>

        <!-- Прогресс воспроизведения -->
        <div class="mt-5 d-flex align-items-center gap-3">
          <span class="text-muted h6 mb-0 progress-time progress-time--left">
            <FormatTime :seconds="currentTime" />
          </span>

          <div
            class="progress-bar flex-grow-1"
            ref="progressBarRef"
            @pointerdown="startDrag"
          >
            <div class="progress h-100" :style="{ width: displayProgress + '%' }"></div>
            <div class="progress-thumb" :style="{ left: displayProgress + '%' }"></div>
          </div>

          <span class="text-muted h6 mb-0 progress-time progress-time--right">
            <FormatTime :seconds="duration" />
          </span>
        </div>

        <!-- Основные элементы управления -->
        <div
          class="position-relative d-flex justify-content-center align-items-center mt-4 text-white mb-3"
        >
          <!-- <button
            class="control-btns"
            :class="{ active: shuffle }"
            @click="$emit('toggleShuffle')"
            :title="$t('audio.controls.shuffle')"
          >
            <i class="bi bi-shuffle"></i>
          </button> -->

          <button class="control-btns" @click="previous">
            <i class="bi bi-skip-backward-fill"></i>
          </button>

          <button
            class="control-btns play-btns"
            @click="togglePlay"
            :title="isPlaying ? $t('media.pause') : $t('media.play')"
          >
            <i :class="isPlaying ? 'bi-pause-fill' : 'bi-play-fill'"></i>
          </button>

          <button class="control-btns" @click="next">
            <i class="bi bi-skip-forward-fill"></i>
          </button>

          <button
            v-if="continueEntry"
            class="control-btns continue-btns position-absolute end-0"
            @click="continuePlayback"
            :title="$t('buttons.continue')"
          >
            <i class="bi bi-play-circle-fill me-2"></i>
            {{ $t("buttons.continue") }}
          </button>

          <!-- <button
            class="control-btns"
            :class="{ active: repeat }"
            @click="$emit('toggleRepeat')"
            :title="
              repeat ? $t('audio.controls.loop_off') : $t('audio.controls.loop_track')
            "
          >
            <i class="bi bi-repeat"></i>
          </button> -->
        </div>
      </div>
    </div>

    <!-- Правая колонка: Плейлист -->
    <div
      class="col-12 audio-playlist-col p-2"
      style="display: flex; flex-direction: column; gap: 1rem"
    >
      <ListHead @shuffle="handleShuffleTrackList">{{ $t("audio.tracks") }}</ListHead>
      <CustomScrollVertical>
        <ListItem
          v-for="t in shuffled"
          :key="t._id"
          :is-active="currentTrack?._id === t._id"
          @click="selectTrack(t)"
          noPadding
        >
          <ListItemAudio
            :title="t.name"
            :artist="t.artist"
            :cover-url="t.coverUrl"
            :selected="currentTrack?._id === t._id"
            :playing="currentTrack?._id === t._id && isPlaying"
            @update:playing="() => handlePlayPauseTrack(t)"
          />
        </ListItem>
      </CustomScrollVertical>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useEventListener } from "@vueuse/core";
import { useAudio } from "@/store/modules/media/audio-list";
import { useAudioControls } from "@/composables/audio/useAudioControls";
import { useShuffle } from "@/composables/useShuffle";
import { useVRoom } from "@/store/modules/hotel/virtual-room";

import type { UseAudioList as Audio } from "@/@types/audio/use_audio";

import FormatTime from "../app/_shared/format/format-time.vue";
import IconComponent from "../app/_shared/icons/icon-component.vue";
import ListHead from "@/views/audio-next/ListHead.vue";
import ListItem from "@/components/common-ui/lists/ListItem.vue";
import ListItemAudio from "@/views/audio-next/ListItemAudio.vue";
import CustomScrollVertical from "@/views/audio-next/CustomScrollVertical.vue";

const audioStore = useAudio();
const progressBarRef = ref<HTMLDivElement | null>(null);
const {
  isPlaying,
  togglePlay,
  currentTrack,
  next,
  previous,
  playlist,
  play,
  setCurrentTime,
} = useAudioControls();

// Получаем shuffled-список и метод shuffle из composable
const { shuffled, shuffle: shuffleTrackList } = useShuffle(playlist);

// ------------ Продолжить (история vroom) ------------
const vroom = useVRoom();
const continueEntry = computed(() => {
  const bookId = currentTrack.value?.path || "";
  if (!bookId) return null;
  const entry = vroom.getVroomAudiobooks.find((b) => b.book_id === bookId);
  if (!entry) return null;
  const time = Number(entry.current_time);
  if (!time || time <= 0) return null;
  return entry;
});

const continuePlayback = () => {
  const entry = continueEntry.value;
  if (!entry) return;

  const time = Number(entry.current_time) || 0;
  const tracks = audioStore.getTrackList;
  const chapter =
    tracks.find((t) => t._id === entry.filename) ||
    tracks[tracks.length - 1] ||
    currentTrack.value;
  if (!chapter) return;

  if (currentTrack.value?._id === chapter._id) {
    if (time > 0) setCurrentTime(time);
    if (!isPlaying.value) togglePlay();
    return;
  }

  const playlistWithTime = tracks.map((t) =>
    t._id === chapter._id ? { ...t, currentTime: time } : t
  );
  play({ track: chapter, playlist: playlistWithTime });
};

// ------------ computed совойства ------------
const currentTime = computed(() => audioStore.currentTime);
const duration = computed(() => audioStore.duration);
const progress = computed(() => audioStore.progress);

// ==================== Перетаскивание бегунка ====================
const isDragging = ref(false);
const seekValue = ref<number | null>(null);

// Локальный процент во время драга, иначе — из стора
const displayProgress = computed(() => {
  if (isDragging.value && seekValue.value !== null) {
    return seekValue.value;
  }
  return progress.value;
});

const calculateProgress = (clientX: number): number => {
  if (!progressBarRef.value || !duration.value) return 0;

  const rect = progressBarRef.value.getBoundingClientRect();
  const pos = (clientX - rect.left) / rect.width; // 0..1
  return Math.max(0, Math.min(100, pos * 100));
};

const startDrag = (event: PointerEvent) => {
  if (!currentTrack.value || !duration.value) return;

  isDragging.value = true;
  seekValue.value = calculateProgress(event.clientX);

  // Предотвращаем выделение текста и стандартные тач-действия при старте драга
  event.preventDefault();
};

// Движение указателя отслеживаем на window — работает и за пределами бара
useEventListener(window, "pointermove", (event: PointerEvent) => {
  if (!isDragging.value) return;
  seekValue.value = calculateProgress(event.clientX);
});

// Отпускание указателя — применяем итоговую позицию
useEventListener(window, "pointerup", (event: PointerEvent) => {
  if (!isDragging.value) return;

  const finalPercent = calculateProgress(event.clientX);
  const seekTime = (finalPercent / 100) * duration.value;
  setCurrentTime(seekTime);

  isDragging.value = false;
  seekValue.value = null;
});

const selectTrack = (track: Audio) => {
  if (!track || !shuffled.value) return;
  play({ track, playlist: [...shuffled.value] });
};

const handlePlayPauseTrack = (track: Audio) => {
  if (currentTrack.value?._id === track._id) {
    togglePlay();
  } else {
    selectTrack(track);
  }
};

// Обработчик шафлинга треков с обновлением очереди (по аналогии с референсом)
const handleShuffleTrackList = () => {
  // 1. Шафлим локальный список для отображения
  shuffleTrackList();

  // 2. Если что-то играет и текущий трек есть в новом шафл-списке — обновляем очередь
  if (isPlaying.value && currentTrack.value) {
    const currentTrackInShuffled = shuffled.value.find(
      (t) => t._id === currentTrack.value?._id
    );

    if (currentTrackInShuffled) {
      play({ track: currentTrackInShuffled, playlist: [...shuffled.value] });
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/sass/variables.scss";

$hover-bg: rgba($white, 0.1);
$btn-bg-secondary: rgba($secondary, 0.05);

@media (min-width: 768px) {
  .audio-player-col {
    flex: 0 0 auto;
    width: 62.1%;
  }

  .audio-playlist-col {
    flex: 0 0 auto;
    width: 37.9%;
  }
}

.progress-bar {
  height: 6px;
  background-color: rgba($white, 0.1);
  border-radius: 3px;
  cursor: pointer;
  position: relative;
  overflow: visible; // переопределяем overflow:hidden из Bootstrap, иначе бегунок обрежется
  touch-action: none; // отключает прокрутку страницы при свайпе по прогресс-бару
  user-select: none; // запрещает выделение текста при драге мышкой

  // Расширяем зону захвата для пальца/мыши, не меняя визуал бара
  &::before {
    content: "";
    position: absolute;
    inset: -12px 0;
  }

  .progress {
    background-color: $white;
    border-radius: 3px;
    position: relative;
    transition: width 0.1s linear;
  }

  // Бегунок — sibling от .progress, не обрезается бордером/overflow дорожки
  .progress-thumb {
    position: absolute;
    top: 50%;
    width: 20px;
    height: 20px;
    background-color: $white;
    border-radius: 50%;
    transform: translate(-50%, -50%);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.4);
    pointer-events: none; // клик обрабатывает контейнер
    z-index: 10;
  }
}

.progress-time {
  min-width: 6ch;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;

  &--left {
    text-align: right;
  }

  &--right {
    text-align: left;
  }
}

.control-btns {
  background: none;
  border: none;
  font-size: 20px;
  margin: 0 15px;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.4s;
  cursor: pointer;
  background-color: $btn-bg-secondary;
  color: $white;

  &:hover {
    background-color: $hover-bg;
  }

  &.active {
    color: darken($white, 10%);
  }

  &.play-btns {
    background-color: $white;
    color: $primary;
    width: 60px;
    height: 60px;
    font-size: 24px;

    &:hover {
      background-color: darken($light, 10%);
      transform: scale(1.05);
    }
  }

  &.continue-btns {
    width: auto;
    padding: 0 20px;
    border-radius: 25px;
    font-size: 16px;
  }
}
</style>
